import React, { createContext, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { useConversationModal } from 'hooks/useConversationModal'
import { ConversationMessage } from 'components/GPTConversation/ConversationModal'

interface IPromptGeneratorContext {
  /** False when there is no active group — the helper cannot be started, so the entry point is hidden. */
  isAvailable: boolean
  /** Called by the step on mount/unmount: connects / disconnects the socket (history is kept). */
  activate: VoidFunction
  deactivate: VoidFunction
  conversation: ConversationMessage[]
  isBotResponseInProgress: boolean
  isWebSocketReady: boolean
  hasWebSocketError: boolean
  isSessionExpired: boolean
  sendMessage: (message: string) => void
  clearHistory: VoidFunction
  reconnect: VoidFunction
  reloadSession: VoidFunction
  stopVoicePlayback: VoidFunction
}

const noop = () => {}

const PromptGeneratorContext = createContext<IPromptGeneratorContext>({
  isAvailable: false,
  activate: noop,
  deactivate: noop,
  conversation: [],
  isBotResponseInProgress: false,
  isWebSocketReady: false,
  hasWebSocketError: false,
  isSessionExpired: false,
  sendMessage: noop,
  clearHistory: noop,
  reconnect: noop,
  reloadSession: noop,
  stopVoicePlayback: noop,
})

export const usePromptGenerator = () => useContext(PromptGeneratorContext)

/**
 * Owns the prompt-writing helper conversation (`CONST_PROMPT_CREATOR_HELPER_PROMPT_ID`).
 *
 * Mounted ABOVE the step switch on purpose: `AI_PROMPT_GENERATOR` is a step, so the step component
 * unmounts on every navigation — keeping the conversation here preserves the history (and the
 * `session_id`) when the user goes back to the prompt and opens the helper again.
 */
export const PromptGeneratorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const activeGroupID = useSelector((state: RootState) => state.config.activeGroupID)

  const {
    openModal,
    closeModal,
    conversation,
    isBotResponseInProgress,
    isWebSocketReady,
    handleSendMessage,
    clearHistory,
    handleReconnect,
    hasWebSocketError,
    isSessionExpired,
    reloadSession,
    stopVoicePlayback,
  } = useConversationModal({
    prompt_id: keys.AI_IMAGE_SUBTASK.CONST_PROMPT_CREATOR_HELPER_PROMPT_ID,
    group_id: activeGroupID ?? undefined,
    isInjectReports: false,
    isInstantConversation: false,
    mode: 0,
    play: false,
    defaultOpen: false,
  })

  const value = useMemo(
    () => ({
      isAvailable: !!activeGroupID,
      activate: openModal,
      deactivate: closeModal,
      conversation,
      isBotResponseInProgress,
      isWebSocketReady,
      hasWebSocketError,
      isSessionExpired,
      sendMessage: handleSendMessage,
      clearHistory,
      reconnect: handleReconnect,
      reloadSession,
      stopVoicePlayback,
    }),
    [
      activeGroupID,
      openModal,
      closeModal,
      conversation,
      isBotResponseInProgress,
      isWebSocketReady,
      hasWebSocketError,
      isSessionExpired,
      handleSendMessage,
      clearHistory,
      handleReconnect,
      reloadSession,
      stopVoicePlayback,
    ]
  )

  return <PromptGeneratorContext.Provider value={value}>{children}</PromptGeneratorContext.Provider>
}
